import { useLayoutEffect, useSyncExternalStore } from "react";
import type { ShapeToolKind } from "@bezpart-board/shared";
import type { BoardToolId } from "../../editor/types";
import {
  getShapeSettings,
  SHAPE_KIND_GROUPS,
  SHAPE_KIND_OPTIONS,
  setShapeSettings,
  shapeSettingsEventName,
} from "../../editor/shapeSession";
import type { ShapesGroupToolId } from "../../editor/shapesGroup";
import { IconArrow, IconLine } from "./BoardToolIcons";
import { ShapeKindIcon } from "./ShapeKindIcon";

function subscribeShapeSettings(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener(shapeSettingsEventName(), handler);
  return () => window.removeEventListener(shapeSettingsEventName(), handler);
}

function notifyShapeSettings() {
  window.dispatchEvent(new Event(shapeSettingsEventName()));
}

type Props = {
  activeTool: BoardToolId;
  onPickTool: (id: ShapesGroupToolId) => void;
};

export function BoardShapeFlyout({ activeTool, onPickTool }: Props) {
  const shape = useSyncExternalStore(
    subscribeShapeSettings,
    getShapeSettings,
    getShapeSettings
  );

  useLayoutEffect(() => {
    window.dispatchEvent(new Event("wb:tool-options-layout"));
  }, []);

  function pickKind(kind: ShapeToolKind) {
    setShapeSettings({ kind });
    notifyShapeSettings();
    onPickTool("shape");
  }

  return (
    <div className="wb-shape-flyout" role="group" aria-label="Тип фигуры">
      <div className="wb-shape-flyout__tools">
        <button
          type="button"
          className={`wb-shape-flyout__btn${activeTool === "line" ? " is-active" : ""}`}
          title="Прямая (L)"
          aria-pressed={activeTool === "line"}
          onClick={() => onPickTool("line")}
        >
          <IconLine className="wb-shape-flyout__icon" />
        </button>
        <button
          type="button"
          className={`wb-shape-flyout__btn${activeTool === "arrow" ? " is-active" : ""}`}
          title="Стрелка (A)"
          aria-pressed={activeTool === "arrow"}
          onClick={() => onPickTool("arrow")}
        >
          <IconArrow className="wb-shape-flyout__icon" />
        </button>
      </div>

      <div className="wb-shape-flyout__library">
        {SHAPE_KIND_GROUPS.map((group) => {
          const items = SHAPE_KIND_OPTIONS.filter((o) => o.group === group.id);
          if (items.length === 0) return null;
          return (
            <section key={group.id} className="wb-shape-flyout__section">
              <h3 className="wb-shape-flyout__section-title">{group.title}</h3>
              <div className="wb-shape-flyout__grid">
                {items.map((opt) => {
                  const active = activeTool === "shape" && shape.kind === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`wb-shape-flyout__btn${active ? " is-active" : ""}`}
                      title={opt.title}
                      aria-label={opt.title}
                      aria-pressed={active}
                      onClick={() => pickKind(opt.id)}
                    >
                      <ShapeKindIcon kind={opt.id} className="wb-shape-flyout__glyph" />
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
